let liveRegion: HTMLDivElement | null = null
let timeoutIds: Array<ReturnType<typeof setTimeout>> = []

type AnnounceOptions = {
  politeness?: 'assertive' | 'polite'
  duration?: number
}

const MESSAGE_TIMEOUT = 100

/**
 * Creates a visually hidden live region for screen reader announcements
 */
const createLiveRegion = (politeness: 'assertive' | 'polite' = 'assertive'): HTMLDivElement => {
  const region = document.createElement('div')
  Object.assign(region.style, {
    position: 'fixed',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
  })
  region.setAttribute('role', 'alert')
  region.setAttribute('aria-atomic', 'true')
  region.setAttribute('aria-live', politeness)
  return region
}

/**
 * Cleans up the live region and any pending timeouts
 */
const cleanupLiveRegion = (): void => {
  if (liveRegion?.parentNode) {
    liveRegion.parentNode.removeChild(liveRegion)
    liveRegion = null
  }
  timeoutIds.forEach(clearTimeout)
  timeoutIds = []
}

/**
 * Ensures the live region is initialized and ready for announcements
 */
const ensureInitialized = (politeness: 'assertive' | 'polite' = 'assertive'): boolean => {
  if (typeof document === 'undefined') {
    // eslint-disable-next-line no-console -- warning about possible design issue
    console.warn('PersistentLiveRegion: document is not defined. Operation skipped.')
    return false
  }

  if (!liveRegion) {
    liveRegion = createLiveRegion(politeness)
    document.body.appendChild(liveRegion)
  }
  return true
}

/**
 * A utility for managing screen reader announcements through a persistent live region.
 * This ensures announcements are properly announced by screen readers while maintaining
 * a clean DOM structure.
 */
export const PersistentLiveRegion = {
  /**
   * Announces a message to screen readers
   */
  announce: (message: string, { politeness = 'assertive', duration = 3000 }: AnnounceOptions = {}): void => {
    if (!ensureInitialized(politeness)) return

    cleanupLiveRegion()
    liveRegion = createLiveRegion(politeness)
    document.body.appendChild(liveRegion)

    // Focus and announce
    liveRegion.setAttribute('tabindex', '-1')
    liveRegion.focus()

    // Set message after a short delay to ensure focus is established
    const messageTimeout = setTimeout(() => {
      if (liveRegion) {
        liveRegion.textContent = message
      }
    }, MESSAGE_TIMEOUT)
    timeoutIds.push(messageTimeout)

    // Cleanup after duration
    const cleanupTimeout = setTimeout(() => {
      cleanupLiveRegion()
    }, duration)
    timeoutIds.push(cleanupTimeout)
  },
}
